import { useCallback, useRef } from 'react';

/**
 * Custom hook for managing action context in infrastructure list components.
 *
 * Provides a ref-based storage for table action handlers and selection state,
 * with a helper function to update the context. This pattern is useful for
 * passing dynamic state to callbacks that need to access the latest values
 * without triggering re-renders.
 *
 * @returns {Object} Action context management object
 * @returns {React.MutableRefObject} .contextRef - Ref containing action context
 * @returns {Function} .updateContext - Function to update context values
 *
 * @example
 * function MyListComponent() {
 *   const { contextRef, updateContext } = useActionContext();
 *
 *   const handleAction = useCallback(() => {
 *     const { handleConfirmedAction, tableSelection } = contextRef.current;
 *     handleConfirmedAction(undefined, 'DELETE', tableSelection);
 *   }, []);
 *
 *   const renderActions = ({ handleConfirmedDataTableAction, tableSelection, tableState }) => {
 *     updateContext({
 *       handleConfirmedAction: handleConfirmedDataTableAction,
 *       tableSelection,
 *       tableState
 *     });
 *     return <ActionButtons />;
 *   };
 * }
 */
export function useActionContext() {
    const contextRef = useRef({
        handleConfirmedAction: null,
        tableSelection: null,
        tableState: null,
    });

    const updateContext = useCallback((updates) => {
        contextRef.current = {
            ...contextRef.current,
            ...updates,
        };
    }, []);

    return { contextRef, updateContext };
}
